import type { KeyboardEvent, MouseEvent } from 'react';
import { WEEKDAYS_SHORT } from '../events/calendar';
import { addDays } from '../events/labels';
import { Link } from 'react-router-dom';
import { AWAITING_WORD, dayLabel, eventUrl, statusTone, taskText, type DayCell } from './myCalendar';

/**
 * „Mój kalendarz” (owner 2026-10-28 21:32: „wolę, żeby to było podobne do głównego okna”): the month as the main
 * calendar draws it — the same frame, weekdays, day numbers and today — with each day's ground the colour of my
 * availability in the grid (D, N, hours), my events as small chips with my status (the side panel's dots, #209) and the
 * question marker in the grid's Z style („?” waiting, „Tak” / „Nie” answered), my open private tasks with a date in their
 * own colour (owner 2026-11-03 23:41). Past days are greyed (#207).
 * A click opens the day; Cmd / Alt-click picks several (`onDay(day, add)`).
 */
export function MyMonth({ weeks, selected, open, onDay, onTask }: { weeks: DayCell[][]; selected: readonly string[]; open: string | null; onDay: (day: string, add: boolean) => void; onTask?: ((id: string) => void) | undefined }) {
  return (
    <div className="cal-month mc-month" data-feature-id="crew.my_availability" onKeyDown={moveFocus}>
      <div className="cal-weekdays" aria-hidden="true">
        {WEEKDAYS_SHORT.map((w) => (
          <div key={w}>{w}</div>
        ))}
      </div>
      {weeks.map((week) => (
        <div key={week[0]!.day} className="mc-week">
          {week.map((cell) => (
            <DayButton key={cell.day} cell={cell} picked={selected.includes(cell.day)} open={open === cell.day} onDay={onDay} onTask={onTask} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Review M9: ← → move a day, ↑ ↓ a week, between the days shown (Enter / Space open the day: they are buttons). */
const STEP: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
function moveFocus(e: KeyboardEvent<HTMLDivElement>) {
  const step = STEP[e.key];
  // review M2: from a day's button or from anything in its cell (a task's chip keeps the focus after a click)
  const from = e.target instanceof HTMLElement ? e.target.closest('.mc-day')?.querySelector<HTMLElement>('[data-day]')?.dataset.day : undefined;
  if (step === undefined || !from || e.altKey || e.metaKey || e.ctrlKey || e.shiftKey) return;
  const next = e.currentTarget.querySelector<HTMLElement>(`[data-day="${addDays(from, step)}"]`);
  if (!next) return;
  e.preventDefault();
  next.focus();
}

/** What a day says to a screen reader: the date, my availability, my events with status, the question. */
function spoken(cell: DayCell): string {
  const parts = [dayLabel(cell.day)];
  if (cell.mark) parts.push(`${cell.mark.state === 'available' ? 'Dostępny' : 'Niedostępny'}${cell.mark.hours ? ` ${cell.mark.hours}` : ''}`);
  for (const e of cell.entries) parts.push(`${e.event.name || 'Wydarzenie'}${e.status ? ` — ${statusTone(e.status).word}` : ''}`);
  for (const a of cell.awaiting) parts.push(`${a.eventName} — ${AWAITING_WORD}`);
  if (cell.question === '?') parts.push('zapytanie bez odpowiedzi');
  // owner 2026-10-30 01:30: the answer as it stands, and that a newer mark changed it
  else if (cell.question) parts.push(`odpowiedź na zapytanie: ${cell.question}${cell.questionNote ? ` (${cell.questionNote})` : ''}`);
  for (const t of cell.tasks) parts.push(`zadanie prywatne: ${taskText(t)}`);
  return parts.join(', ');
}

/** The event's name in a chip: a link to it in the main calendar when I may open it (owner 00:55), else plain text. */
function ChipName({ name, to }: { name: string; to: string | null }) {
  return to ? (
    <Link className="mc-chip-name" to={to} tabIndex={-1}>
      {name}
    </Link>
  ) : (
    <span className="mc-chip-name">{name}</span>
  );
}

/**
 * A day: the cell, its button over the whole of it (the number, my mark, the question marker; a click opens the day)
 * and my events' chips beside the button — a chip's name may be a link (owner 00:55: „niech ten napis będzie
 * linkiem”), which a button cannot hold; a click on the rest of a chip opens the day too.
 */
function DayButton({ cell, picked, open, onDay, onTask }: { cell: DayCell; picked: boolean; open: boolean; onDay: (day: string, add: boolean) => void; onTask?: ((id: string) => void) | undefined }) {
  const cls = ['mc-day', cell.inMonth ? '' : 'outside', cell.past ? 'past' : '', cell.today ? 'today' : '', picked ? 'picked' : '', open ? 'open' : ''].filter(Boolean).join(' ');
  return (
    // the click is the cell's (the button's bubbles here, so do Enter / Space on it): a chip's link only goes to the event
    // a private task's chip opens the task (owner 2026-11-03 23:45), not the day
    <div className={cls} data-state={cell.mark?.state} onClick={(e: MouseEvent<HTMLDivElement>) => !(e.target instanceof Element && e.target.closest('a, .mc-chip.task')) && onDay(cell.day, e.metaKey || e.altKey || e.ctrlKey)}>
      <button
        type="button"
        className="mc-day-hit"
        data-day={cell.day}
        data-state={cell.mark?.state}
        aria-label={spoken(cell)}
        aria-pressed={picked}
      >
        <span className="mc-day-top">
          <span className="cal-day-num">{Number(cell.day.slice(8, 10))}</span>
          {cell.question && (
            <span className={`mc-q${cell.question === '?' ? ' waiting' : cell.question === 'Tak' ? ' yes' : ' no'}${cell.questionNote ? ' changed' : ''}`} title={cell.questionNote ? `${cell.question} (${cell.questionNote})` : undefined} aria-hidden="true">
              {cell.question}
            </span>
          )}
        </span>
        {cell.mark && (
          <span className="mc-mark" aria-hidden="true">
            <b>{cell.mark.state === 'available' ? 'D' : 'N'}</b>
            {cell.mark.hours && <small>{cell.mark.hours}</small>}
          </span>
        )}
      </button>
      {(cell.entries.length > 0 || cell.awaiting.length > 0 || cell.tasks.length > 0) && (
        <span className="mc-chips">
          {cell.entries.map((e) => {
            const tone = e.status ? statusTone(e.status) : null;
            const name = e.event.name || 'Wydarzenie';
            return (
              <span key={e.id} className={`mc-chip${e.status === 'odmowa' ? ' refused' : ''}`} title={`${name}${tone ? ` — ${tone.word}` : ''}`}>
                {tone && <i className="ep-dot" data-tone={tone.tone} aria-hidden="true" />}
                <ChipName name={name} to={e.canOpen ? eventUrl(e.event.id) : null} />
              </span>
            );
          })}
          {cell.awaiting.map((a) => (
            <span key={`w:${a.questionId}`} className="mc-chip awaiting" title={`${a.eventName} — ${AWAITING_WORD}`}>
              <i className="ep-dot" data-tone="muted" aria-hidden="true" />
              <ChipName name={a.eventName} to={a.canOpen && a.eventId ? eventUrl(a.eventId) : null} />
              <small className="mc-chip-status">{AWAITING_WORD}</small>
            </span>
          ))}
          {/* owner 2026-11-03 23:41: my private tasks of the day, in their own colour; a click opens one for editing */}
          {cell.tasks.map((t) => (
            <button key={`t:${t.id}`} type="button" className="mc-chip task" tabIndex={-1} title={taskText(t)} aria-label={`Zadanie prywatne: ${taskText(t)}`} onClick={() => onTask?.(t.id)} data-feature-id="private-tasks.list">
              {t.dueTime && <small className="mc-chip-time">{t.dueTime}</small>}
              <span className="mc-chip-name">{t.title}</span>
            </button>
          ))}
        </span>
      )}
    </div>
  );
}
